import { useContext, useEffect, useState } from "react";
import axiosInstance from "../../utilities/Axios";
import AlertDialog from "../AlertDialog/AlertDialog";
import { EDialogAction } from "../Enum/DialogActionEnum";
import CountryTemplateDialogBox from "./CountryTemplateDialogBox";
import useQuery from "../../Hooks/useQueryHook/useQueryHook";
import { debounceTime } from "rxjs/operators";
import { Subject } from "rxjs";
import { Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import { useTheme } from "../../Hooks/ThemeContext/ThemeContext";
import { DeleteOutline, EditOutlined, OpenInNew } from "@mui/icons-material";
import { LoadingStateContext } from "../../Hooks/LoadingContext/LoadingContext";
import { CountryProcessDisplayLabelEnum, CountryProcessTemplateTypeEnum } from "../../Types/Enum";

const useDebounce = (time: number, initialValue: any) => {
  const [value, setValue] = useState(initialValue);
  const [values] = useState(() => new Subject());
  useEffect(() => {
    const sub = values.pipe(debounceTime(time)).subscribe(setValue);
    return () => sub.unsubscribe();
  }, [time, values]);
  return [value, (v: any) => values.next(v)];
};

const CountryTemplateTable = (props: { filter: any }) => {
  const query = useQuery();
  const [countryTemplate, setCountryTemplate] = useState(null);
  const [countryTemplateList, setCountryTemplateList] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [editDialogVisibility, setEditDialogVisibility] = useState(false);
  const [alertDialogVisibility, setAlertDialogVisibility] = useState(false);
  const [dialogAction, setDialogAction] = useState(EDialogAction.view);
  const [orderArray, setOrderArray] = useState<any[]>([]);
  const { changeLoading, isLoading } = useContext(LoadingStateContext);
  const theme = useTheme()
  let max = 99;
  const [order, setOrder] = useState(0);

  useEffect(() => {
    getCountryTemplateList();
  }, [page, props.filter, limit]);

  const getCountryTemplateList = async () => {
    changeLoading(true);
    try {
      const response = await axiosInstance.get(`/v1/country-process-tpl`, {
        params: {
          country_process_id:  query.get("countryProcessId"),
          page: page,
          items_per_page: limit
        },
      });

      const data = response.data;
      setCountryTemplateList(data.result.data);

      setTotalItems(data.result.total);
      if (orderArray.length != 0) {
        max = orderArray.reduce((a, b) => {
          return Math.max(a, b);
        });
      }
      setOrder(max);
    } catch (err) {
      console.log(err);
    } finally {
      changeLoading(false);
    }
  };
  const sortedCountryTemplateList = countryTemplateList
    .slice()
    .sort((a, b) => a.order - b.order);

  const getCountryTemplateByProcessId = async (
    countryTemplateId: number,
    action: EDialogAction
  ) => {
    try {
      const response = await axiosInstance.get(
        `/v1/country-process-tpl/${countryTemplateId}`,
        {}
      );

      const data = response.data;
      setCountryTemplate(data.result);

      if (action == EDialogAction.edit) {
        setEditDialogVisibility(true);
      } else if (action == EDialogAction.view) {
        setEditDialogVisibility(true);
      } else if (action == EDialogAction.delete) {
        setAlertDialogVisibility(true);
      }
      setDialogAction(action);
    } catch (err) {
      console.log(err);
    } 
  };

  const goToNextPage = () => {
    setPage(page + 1);
  };

  const goToPreviousPage = () => {
    setPage(page - 1);
  };

  function editDialogClose(action: EDialogAction) {
    setEditDialogVisibility(false);
    if (action == EDialogAction.edit) {
    } else if (action == EDialogAction.view) {
    } else if (action == EDialogAction.delete) {
    }
    getCountryTemplateList();
  }
  function alertDialogClose(action: EDialogAction) {
    setAlertDialogVisibility(false);
    getCountryTemplateList();
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <CountryTemplateDialogBox
        open={editDialogVisibility}
        onClose={editDialogClose}
        countryProcessId=""
        data={countryTemplate}
        dialogAction={dialogAction}
      />
      <AlertDialog
        openAlert={alertDialogVisibility}
        onCloseAlert={alertDialogClose}
        data={countryTemplate}
        action="template"
      />
      <Paper>

        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Country Process</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress/></TableCell>
                </TableRow>
              )}
              {!isLoading && sortedCountryTemplateList.length ==0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">No data found.</TableCell>
                </TableRow>
              )}
              
              {!isLoading && sortedCountryTemplateList != null ? (
                sortedCountryTemplateList.map((data: any, key: number) => (
                  <TableRow key={data.id}>
                    <TableCell>{data.id}</TableCell>
                    <TableCell>{data.country_process_id}</TableCell>
                    <TableCell>{CountryProcessDisplayLabelEnum[data.title]}</TableCell>
                    <TableCell>{data.type}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          getCountryTemplateByProcessId(
                            data.id,
                            EDialogAction.view
                          );
                        }}
                      >
                        <OpenInNew/>
                      </Button>{" "}
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          getCountryTemplateByProcessId(
                            data.id,
                            EDialogAction.edit
                          );
                        }}
                      >
                        <EditOutlined/>
                      </Button>{" "}
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                          getCountryTemplateByProcessId(
                            data.id,
                            EDialogAction.delete
                          );
                        }}
                      >
                        <DeleteOutline/>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : null}
            </TableBody>
            <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            count={totalItems}
            rowsPerPage={limit}
            page={page}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          </Table>
          
        </TableContainer>
      </Paper>
    </>
  );
};

export default CountryTemplateTable;
